/*
 *
 * EconomicConditionsSelectorModal
 *
 * @version 1.0.0
 * @author Patrizia Petrera
 * @since 24-Sep-2025
 */

import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'
import { useFormik } from 'formik'
import Toggle from 'Hoc/ToggleComponent'
import MerchantGenericCard from '@nexidigital/merchantgenericcard'
import MerchantModal from '@nexidigital/merchantmodal'
import PortalSelect from '@nexidigital/portalselect'
import { mapShopType } from 'Utility/utils'
import formattedMessage from './languageModule'
import './EconomicConditionsSelectorModal.scss'
import { capitalizeFirst } from 'Utility/globalMethods'
import MerchantTableBadge from '@nexidigital/merchanttablebadge'
import PortalLoader from '@nexidigital/portalloader'

// Field constants
const SHOPS = 'shops'
const ABI = 'abi'
const OFFERS = 'offers'

const EconomicConditionsSelectorModal = (props) => {
  const intl = useIntl()

  // Track if ABI has been changed by the user to control loader visibility
  const [abiChangedByUser, setAbiChangedByUser] = useState(false)

  // Static helper functions - they never change with Redux data
  const getFilteredAbi = (shopId) => {
    if (!shopId || !props.shops) return []
    const selectedShop = props.shops.find((shop) => shop.shopId === shopId)
    return selectedShop?.abiList || []
  }

  // Offers are now provided by a separate API based on selected ABI
  const getFilteredOffers = () => props.offerList || []

  const getShopNameById = (shopId) => {
    const shop = props.shops?.find((shop) => shop.shopId === shopId)
    return shop?.shopName || ''
  }

  const getBankNameByCode = (abiCode) => {
    for (const shop of props.shops || []) {
      const abi = shop.abiList?.find((abi) => abi.abiCode === abiCode)
      if (abi) return abi.bankName || ''
    }
    return ''
  }

  const getOfferNameById = (offerId) => {
    const offer = (props.offerList || []).find((o) => o.offerId === offerId)
    return offer?.offerName || ''
  }

  // Compute initial values with optional prefill (from EconomicConditions box)
  const getInitialValues = () => {
    const defaults = { [SHOPS]: '', [ABI]: '', [OFFERS]: '' }

    // If prefill values are provided (only when opened from EconomicConditions box), try to use them
    const prefill = props.prefillValues || null
    if (prefill) {
      const shopIdPref = prefill.shopId || ''
      const abiPref = prefill.abiCode || ''
      const offerPref = prefill.offerId || ''

      // Validate prefill against current data to avoid inconsistent state
      const shopExists = !!(props.shops || []).find((s) => s.shopId === shopIdPref)
      const abiExists = shopExists && !!getFilteredAbi(shopIdPref).find((a) => a.abiCode === abiPref)
      // Offer validation is deferred to when the offers are fetched
      if (shopExists) {
        return {
          [SHOPS]: shopIdPref,
          [ABI]: abiExists ? abiPref : '',
          [OFFERS]: offerPref
        }
      }
      // if shop not found, fall back to auto-selection
    }

    if (!props.shops?.length) return defaults

    // Auto-select if there is only one shop
    const shopId = props.shops.length === 1 ? props.shops[0].shopId : ''
    let abiCode = ''

    if (shopId) {
      const abiList = getFilteredAbi(shopId)
      // Auto-select if there is only one ABI
      if (abiList.length === 1) {
        abiCode = abiList[0].abiCode
      }
    }

    // Offers are fetched after ABI selection; do not preselect here
    return { [SHOPS]: shopId, [ABI]: abiCode, [OFFERS]: '' }
  }

  // Simple validation
  const validateForm = (values) => {
    const errors = {}

    // Shop required if there is more than one option
    if (props.shops?.length > 1 && !values[SHOPS]) {
      errors[SHOPS] = formattedMessage.requiredError
    }

    // ABI required if there is more than one option for the selected shop
    if (values[SHOPS]) {
      const abiList = getFilteredAbi(values[SHOPS])
      if (abiList.length > 1 && !values[ABI]) {
        errors[ABI] = formattedMessage.requiredError
      }

      // Offer required if there is more than one option for the selected ABI
      if (values[ABI]) {
        const offerList = getFilteredOffers()
        if (offerList.length > 1 && !values[OFFERS]) {
          errors[OFFERS] = formattedMessage.requiredError
        }
      }
    }

    return errors
  }

  // Simplified Formik setup
  // Memoize initial values so Formik doesn't reinitialize on every render
  const memoInitialValues = useMemo(() => getInitialValues(), [props.prefillValues, props.shops])

  const formik = useFormik({
    initialValues: memoInitialValues,
    validate: validateForm,
    onSubmit: (values) => {
      // Block submission while offers are loading to ensure all values are available
      if (props.isOffersLoading) {
        return
      }
      // Ensure mono-option values are resolved and submitted
      let resolvedShopId = values[SHOPS]
      let resolvedPaymentContractId = props.shops?.find(shop => shop.shopId === resolvedShopId)?.paymentContractId || ''
      if (!resolvedShopId && props.shops?.length === 1) {
        resolvedShopId = props.shops[0].shopId
        resolvedPaymentContractId = props.shops[0].paymentContractId
      }

      let resolvedAbi = values[ABI]
      if (resolvedShopId && !resolvedAbi) {
        const abiList = getFilteredAbi(resolvedShopId)
        if (abiList.length === 1) {
          resolvedAbi = abiList[0].abiCode
        }
      }

      let resolvedOffer = values[OFFERS]
      if (!resolvedOffer) {
        const offerList = getFilteredOffers()
        if (offerList.length === 1) {
          resolvedOffer = offerList[0].offerId
        }
      }

      const formattedValues = {
        shopId: resolvedShopId || '',
        paymentContractId: resolvedPaymentContractId || '',
        shopName: getShopNameById(resolvedShopId),
        abiCode: resolvedAbi || '',
        bankName: getBankNameByCode(resolvedAbi),
        offerId: resolvedOffer || '',
        offerName: getOfferNameById(resolvedOffer)
      }

      // Avoid submit without all 3 values: shop, abi, and offer
      if ((props.shops.length > 0 && !formattedValues.shopId) || (getFilteredAbi(resolvedShopId).length > 0 && !formattedValues.abiCode) || (getFilteredOffers().length > 0 && !formattedValues.offerId)) {
        return
      }
      props.actions.doGetContract(formattedValues)
    },
    validateOnChange: true,
    validateOnBlur: true
  })

  // Fetch offers whenever ABI is set/changes (including initial prefill/auto-selection)
  useEffect(() => {
    const shopId = formik.values[SHOPS]
    const abiCode = formik.values[ABI]
    if (shopId && abiCode) {
      const paymentContractId = props.shops?.find((shop) => shop.shopId === shopId)?.paymentContractId || null
      props.actions.doGetEconomicConditionsOffers({ shopId, abiCode, paymentContractId })
    }
  }, [formik.values[ABI], formik.values[SHOPS]])

  // Prefilled offer applies only when matches current Shop + ABI selection
  const hasPrefilledOffer = Boolean(
    props?.prefillValues?.offerId &&
    props?.prefillValues?.abiCode === formik.values[ABI] &&
    props?.prefillValues?.shopId === formik.values[SHOPS]
  )

  // Any prefilled offer present, regardless of current selection
  const hasAnyPrefilledOffer = Boolean(props?.prefillValues?.offerId)

  // Build offer options; when prefilled and not yet loaded, show the prefilled value as a temporary option
  const offerOptions = useMemo(() => {
    const offers = getFilteredOffers()
    if ((offers || []).length > 0) {
      return offers.map((offer) => ({ label: capitalizeFirst(offer.offerName || ''), value: offer.offerId || '' }))
    }
    if (hasPrefilledOffer) {
      return [
        {
          label: capitalizeFirst(props.prefillValues?.offerName || props.prefillValues?.offerId || ''),
          value: props.prefillValues?.offerId || ''
        }
      ]
    }
    return []
  }, [props.offerList, props.prefillValues, hasPrefilledOffer])

  // Auto-select offer when only one option is available after fetch
  useEffect(() => {
    const offers = props.offerList || []
    if (!formik.values[ABI]) return

    // If exactly one offer and no selection yet, auto-select it
    if (offers.length === 1 && !formik.values[OFFERS]) {
      formik.setFieldValue(OFFERS, offers[0].offerId)
      return
    }

    // If multiple offers and current selection is not in the list, clear it
    if (offers.length > 1 && formik.values[OFFERS]) {
      const exists = !!offers.find((o) => o.offerId === formik.values[OFFERS])
      if (!exists) {
        formik.setFieldValue(OFFERS, '')
      }
    }
  }, [props.offerList])

  // Field change handler with auto-selection
  const handleFieldChange = (e, fieldName) => {
    const value = e?.value || ''

    if (fieldName === SHOPS) {
      // Reset ABI and OFFERS when the shop changes
      let abiCode = ''

      if (value) {
        const abiList = getFilteredAbi(value)
        // Auto-select if there is only one ABI
        if (abiList.length === 1) {
          abiCode = abiList[0].abiCode
        }
      }

      // Reset the entire form (errors, touched, submitCount) while keeping the new values
      formik.resetForm({ values: { [SHOPS]: value, [ABI]: abiCode, [OFFERS]: '' } })

      // Offers will be fetched by effect when ABI value is set
    } else if (fieldName === ABI) {
      // Reset OFFERS when the ABI changes
      // Reset the entire form (errors, touched, submitCount) while keeping the new values
      formik.resetForm({ values: { ...formik.values, [ABI]: value, [OFFERS]: '' } })
      setAbiChangedByUser(true)

      // Offers will be fetched by effect when ABI value is set
    } else {
      formik.setFieldValue(fieldName, value)
    }
  }

  // Handle blur to trigger error display
  const handleFieldBlur = (fieldName) => {
    formik.setFieldTouched(fieldName, true, true)
  }

  // Determine if a field should be shown and if there is exactly one option, hide the field (auto-selected)
  const shouldShowField = (fieldName, parentValue = null) => {
    switch (fieldName) {
      case SHOPS:
        return props.shops?.length > 1
      case ABI:
        return parentValue && getFilteredAbi(parentValue).length > 1
      case OFFERS:
        // Hide offer input in mono-offer flows even if prefilled
        if (!parentValue) return false
        if (props.isMonoOffer) return false
        return (!props.isOffersLoading && getFilteredOffers().length > 1) || hasPrefilledOffer
      default:
        return false
    }
  }

  // Reset the user-change flag when offers loading completes
  useEffect(() => {
    if (!props.isOffersLoading) {
      setAbiChangedByUser(false)
    }
  }, [props.isOffersLoading])

  const handleCloseModal = () => {
    props.actions.doHidePopup()
  }

  return (
    <Toggle>
      <MerchantModal
        topBarTitle={formattedMessage.modalCustomName}
        onClose={handleCloseModal}
        primaryAction={() => formik.handleSubmit()}
        primaryButtonLabel={formattedMessage.confirm}
        primaryButtonLoading={props.isSubmitting}
        primaryButtonDisabled={props.isSubmitting}
      >
        <div className='economicConditionsSelectorModal'>
          <p className='modalEditNameSubtitle'>{formattedMessage.modalEditNameSubtitle}</p>
          <MerchantGenericCard empty>
            {/* Shop Selection */}
            {shouldShowField(SHOPS) && (
              <PortalSelect
                noSearch
                id={SHOPS}
                name={SHOPS}
                label={intl.formatMessage({
                  id: 't.app.shopName'
                })}
                placeholder={intl.formatMessage({
                  id: 't.app.costs.economic.conditions.modal.shops.placeholder'
                })}
                options={(props.shops || []).map((shop) => ({
                  label: capitalizeFirst(shop.shopName || ''),
                  extendedLabel: (
                    <div className='conditionShopOption'>
                      <span className='conditionShopName'>{capitalizeFirst(shop.shopName || '')}</span>
                      <div className='conditionShopDetails'>
                        <p>{intl.formatMessage({ id: 't.app.costs.economic.conditions.modal.paymentContract' })}</p>
                        <div>
                          <MerchantTableBadge label={<b>{shop.paymentContractId || ''}</b>} status='neutralLight' />
                        </div>
                      </div>
                      <div className='conditionShopTypeDetails'>
                        <p>{intl.formatMessage({ id: 't.app.costs.economic.conditions.modal.shopType' })}</p>
                        <div>
                          <MerchantTableBadge label={<b>{mapShopType(shop.shopType, intl)}</b>} symbol status='brand' />
                        </div>
                      </div>
                    </div>
                  ),
                  value: shop.shopId || ''
                }))}
                value={formik.values[SHOPS]}
                error={(formik.touched[SHOPS] || formik.submitCount > 0) && formik.errors[SHOPS]}
                errorText={(formik.touched[SHOPS] || formik.submitCount > 0) && formik.errors[SHOPS]}
                onChange={(e) => handleFieldChange(e, SHOPS)}
                onBlur={() => handleFieldBlur(SHOPS)}
              />
            )}

            {/* ABI Selection */}
            {shouldShowField(ABI, formik.values[SHOPS]) && (
              <div className='abiSelectContainer'>
                <PortalSelect
                  noSearch
                  id={ABI}
                  name={ABI}
                  label={intl.formatMessage({
                    id: 't.app.costs.economic.conditions.modal.abiCode'
                  })}
                  placeholder={intl.formatMessage({
                    id: 't.app.costs.economic.conditions.modal.selectAbiPlaceholder'
                  })}
                  options={getFilteredAbi(formik.values[SHOPS]).map((abi) => ({
                    label: capitalizeFirst(abi.bankName || ''),
                    extendedLabel: (
                      <div className='conditionAbiOption'>
                        <p className='conditionAbiBankName'>{capitalizeFirst(abi.bankName || '')}</p>
                        <div className='conditionAbiDetails'>
                          {intl.formatMessage({
                            id: 't.app.costs.economic.conditions.modal.abiOptions'
                          })}
                          <b>{abi.abiCode}</b>
                        </div>
                      </div>
                    ),
                    value: abi.abiCode || ''
                  }))}
                  value={formik.values[ABI]}
                  error={(formik.touched[ABI] || formik.submitCount > 0) && formik.errors[ABI]}
                  errorText={(formik.touched[ABI] || formik.submitCount > 0) && formik.errors[ABI]}
                  onChange={(e) => handleFieldChange(e, ABI)}
                  onBlur={() => handleFieldBlur(ABI)}
                />
              </div>
            )}

            {/* Offers Selection */}
            {formik.values[ABI] && props.isOffersLoading && (abiChangedByUser || (!hasPrefilledOffer && !(props.isMonoOffer && hasAnyPrefilledOffer))) && (
              <div style={{ marginTop: 8 }}>
                <PortalLoader circular size='small' centered />
              </div>
            )}
            {shouldShowField(OFFERS, formik.values[ABI]) && (
              <PortalSelect
                noSearch
                id={OFFERS}
                name={OFFERS}
                label={intl.formatMessage({
                  id: 't.app.costs.economic.conditions.modal.offerLabel'
                })}
                placeholder={intl.formatMessage({
                  id: 't.app.costs.economic.conditions.modal.selectOfferPlaceholder'
                })}
                options={offerOptions}
                value={formik.values[OFFERS]}
                error={(formik.touched[OFFERS] || formik.submitCount > 0) && formik.errors[OFFERS]}
                errorText={(formik.touched[OFFERS] || formik.submitCount > 0) && formik.errors[OFFERS]}
                onChange={(e) => handleFieldChange(e, OFFERS)}
                onBlur={() => handleFieldBlur(OFFERS)}
              />
            )}
          </MerchantGenericCard>
        </div>
      </MerchantModal>
    </Toggle>
  )
}

EconomicConditionsSelectorModal.propTypes = {
  actions: PropTypes.object,
  shops: PropTypes.array,
  offerList: PropTypes.array,
  isSubmitting: PropTypes.bool,
  prefillValues: PropTypes.object,
  isOffersLoading: PropTypes.bool,
  isMonoOffer: PropTypes.bool
}

EconomicConditionsSelectorModal.defaultProps = {
  actions: {},
  shops: [],
  offerList: [],
  isSubmitting: false,
  prefillValues: {},
  isOffersLoading: false,
  isMonoOffer: false
}

export default EconomicConditionsSelectorModal
